import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Center, Loader } from "@mantine/core";
import { AuthProvider } from "./contexts/AuthContext.js";
import { Layout } from "./components/Layout.js";
import { ProtectedRoute } from "./components/ProtectedRoute.js";
import { ErrorBoundary } from "./components/ErrorBoundary.js";
import { LoginPage } from "./pages/LoginPage.js";
import { DashboardPage } from "./pages/DashboardPage.js";
const MyProfilePage = lazy(() =>
  import("./pages/MyProfilePage.js").then((m) => ({ default: m.MyProfilePage })),
);
const SettingsPage = lazy(() =>
  import("./pages/SettingsPage.js").then((m) => ({ default: m.SettingsPage })),
);

const MembersPage = lazy(() =>
  import("./pages/MembersPage.js").then((m) => ({ default: m.MembersPage })),
);
const MemberDetailPage = lazy(() =>
  import("./pages/MemberDetailPage.js").then((m) => ({ default: m.MemberDetailPage })),
);
const MemberByWebsiteIdRedirect = lazy(() =>
  import("./pages/MemberByWebsiteIdRedirect.js").then((m) => ({
    default: m.MemberByWebsiteIdRedirect,
  })),
);
const RoleMappingsPage = lazy(() =>
  import("./pages/RoleMappingsPage.js").then((m) => ({ default: m.RoleMappingsPage })),
);
const AuditLogPage = lazy(() =>
  import("./pages/AuditLogPage.js").then((m) => ({ default: m.AuditLogPage })),
);
const CompliancePage = lazy(() =>
  import("./pages/CompliancePage.js").then((m) => ({ default: m.CompliancePage })),
);
const ServerPlayersPage = lazy(() => import("./pages/ServerPlayersPage.js"));
const PendingApprovalsPage = lazy(() =>
  import("./pages/PendingApprovalsPage.js").then((m) => ({ default: m.PendingApprovalsPage })),
);
const MyRequestsPage = lazy(() =>
  import("./pages/MyRequestsPage.js").then((m) => ({ default: m.MyRequestsPage })),
);
const DepartmentsPage = lazy(() =>
  import("./pages/DepartmentsPage.js").then((m) => ({ default: m.DepartmentsPage })),
);
const DepartmentDetailPage = lazy(() =>
  import("./pages/DepartmentDetailPage.js").then((m) => ({ default: m.DepartmentDetailPage })),
);
const DepartmentOverviewEditPage = lazy(() =>
  import("./pages/DepartmentOverviewEditPage.js").then((m) => ({
    default: m.DepartmentOverviewEditPage,
  })),
);
const SubdivisionDetailPage = lazy(() =>
  import("./pages/SubdivisionDetailPage.js").then((m) => ({ default: m.SubdivisionDetailPage })),
);
const CommunityPage = lazy(() =>
  import("./pages/CommunityPage.js").then((m) => ({ default: m.CommunityPage })),
);
const HubPermissionsPage = lazy(() =>
  import("./pages/HubPermissionsPage.js").then((m) => ({ default: m.HubPermissionsPage })),
);
const FormEditorPage = lazy(() =>
  import("./pages/FormEditorPage.js").then((m) => ({ default: m.FormEditorPage })),
);
const FormSubmitPage = lazy(() =>
  import("./pages/FormSubmitPage.js").then((m) => ({ default: m.FormSubmitPage })),
);
const FormResponsesPage = lazy(() =>
  import("./pages/FormResponsesPage.js").then((m) => ({ default: m.FormResponsesPage })),
);
const TakeTestPage = lazy(() => import("./pages/TakeTestPage.js"));
const TestAttemptResultPage = lazy(() => import("./pages/TestAttemptResultPage.js"));
const PracticalEvaluationPage = lazy(() => import("./pages/PracticalEvaluationPage.js"));
const FeedbackPage = lazy(() =>
  import("./pages/FeedbackPage.js").then((m) => ({ default: m.FeedbackPage })),
);
const FeedbackBoardPage = lazy(() =>
  import("./pages/FeedbackBoardPage.js").then((m) => ({ default: m.FeedbackBoardPage })),
);
const FeedbackPostPage = lazy(() =>
  import("./pages/FeedbackPostPage.js").then((m) => ({ default: m.FeedbackPostPage })),
);

const PageFallback = () => (
  <Center h="60vh">
    <Loader size="sm" />
  </Center>
);

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />

            {/* Protected shell */}
            <Route
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="my-profile" element={<MyProfilePage />} />
              <Route path="settings" element={<SettingsPage />} />

              <Route
                path="members"
                element={
                  <ProtectedRoute permissionKey="member.view_list">
                    <MembersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="members/by-website-id/:websiteId"
                element={
                  <ProtectedRoute permissionKey="member.view_list">
                    <MemberByWebsiteIdRedirect />
                  </ProtectedRoute>
                }
              />
              <Route
                path="members/:id"
                element={
                  <ProtectedRoute permissionKey="member.view_list">
                    <MemberDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="role-mappings"
                element={
                  <ProtectedRoute permissionKey="role_mappings.manage">
                    <RoleMappingsPage />
                  </ProtectedRoute>
                }
              />
              <Route path="feedback" element={<FeedbackPage />} />
              <Route path="feedback/:boardSlug" element={<FeedbackBoardPage />} />
              <Route path="feedback/:boardSlug/:postId" element={<FeedbackPostPage />} />
              <Route path="community" element={<CommunityPage />} />
              <Route path="departments" element={<DepartmentsPage />} />
              <Route path="departments/:id" element={<DepartmentDetailPage />} />
              <Route
                path="departments/:id/edit-overview"
                element={<DepartmentOverviewEditPage />}
              />
              <Route
                path="departments/:departmentId/subdivisions/:subId"
                element={<SubdivisionDetailPage />}
              />
              <Route
                path="departments/:deptId/forms/new"
                element={
                  <ErrorBoundary verbose>
                    <FormEditorPage />
                  </ErrorBoundary>
                }
              />
              <Route
                path="departments/:deptId/forms/:formId/edit"
                element={
                  <ErrorBoundary verbose>
                    <FormEditorPage />
                  </ErrorBoundary>
                }
              />
              <Route
                path="departments/:deptId/forms/:formId/submit"
                element={
                  <ErrorBoundary verbose>
                    <FormSubmitPage />
                  </ErrorBoundary>
                }
              />
              <Route
                path="departments/:deptId/forms/:formId/responses"
                element={
                  <ErrorBoundary verbose>
                    <FormResponsesPage />
                  </ErrorBoundary>
                }
              />
              <Route
                path="admin/server"
                element={
                  <ProtectedRoute minRole="staff">
                    <ServerPlayersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/compliance"
                element={
                  <ProtectedRoute permissionKey="compliance.view">
                    <CompliancePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="my-requests"
                element={
                  <ProtectedRoute>
                    <MyRequestsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/pending-approvals"
                element={
                  <ProtectedRoute permissionKey="da_update.request">
                    <PendingApprovalsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/audit-log"
                element={
                  <ProtectedRoute permissionKey="audit_log.view">
                    <AuditLogPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/hub-permissions"
                element={
                  <ProtectedRoute minRole="development_command">
                    <HubPermissionsPage />
                  </ProtectedRoute>
                }
              />
              <Route path="training" element={<Navigate to="/departments" replace />} />
              <Route
                path="training/certifications"
                element={<Navigate to="/departments" replace />}
              />
              <Route path="training/courses" element={<Navigate to="/departments" replace />} />
              <Route path="training/sessions" element={<Navigate to="/departments" replace />} />
              <Route
                path="training/tests/:testId/take"
                element={
                  <ProtectedRoute>
                    <TakeTestPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="training/test-attempts/:attemptId"
                element={
                  <ProtectedRoute>
                    <TestAttemptResultPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="training/practicals/:evalId"
                element={
                  <ProtectedRoute>
                    <PracticalEvaluationPage />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}
